// @ts-nocheck

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import {
  Box,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  MenuItem,
  Select,
  Typography,
  useTheme,
  Tooltip,
  Divider,
} from '@mui/material';
import { SolyticsButton } from '../../../package/components';
import { ReactComponent as CloseIcon } from '../../../assets/Icons/lucideIcons/x.svg';
import { ReactComponent as DescriptionOutlinedIcon } from '../../../assets/Icons/lucideIcons/file-text.svg';
import { ReactComponent as ExpandMoreIcon } from '../../../assets/Icons/lucideIcons/chevron-down.svg';
import { ReactComponent as InfoOutlinedIcon } from '../../../assets/Icons/lucideIcons/info.svg';
import { Handle, Position, useViewport } from 'reactflow';
import upperCase from 'lodash/upperCase';
import PropTypes from 'prop-types';
import { ReactComponent as AttributeIcon } from '../../../assets/Icons/Global Attributes.svg';
import DynamicEntityIcon from '../../../utils/entityIconUtils';
import AlertHealthIndicator from '../ModelInventoryAndAssociation/AlertHealthIndicator';
import {
  EMPTY_ARRAY,
  getFilteredEntityTypes,
} from '../../../const/CommonConst.js';
import {
  createEntityTypeMap,
  normalizeEntityTypeKey,
} from '../../../utils/entityTypeUtils.js';
import { useContextMenu } from 'react-contexify';
import ReactMuiTableListViewV8 from '../ReactMuiTableListViewV8';
import { getEntityDetailsRoutePath } from '../../../utils/inventoryRouteEngine.js';
import { DEFAULT_ROUTE_CONFIG } from '../../../const/defaultRoutesConfig';
import { getEditTemplatePath } from '../../../utils/labels';

export const getEntityLabelById = (entityTypeId) => {
  const matchedEntityType = (getFilteredEntityTypes() || EMPTY_ARRAY).find(
    (entityType) => entityType?.value === entityTypeId
  );

  return matchedEntityType?.label;
};

export const EntityIcon = ({
  entity_type,
  size = 21,
  wrapperSize = 30,
  showBackground = true,
}) => {
  const theme = useTheme();
  const dynamicEntityTypes = useMemo(() => {
    return getFilteredEntityTypes() || [];
  }, []);

  const matchedDynamicEntityType = useMemo(() => {
    const normalizedEntityType = String(entity_type || '')
      .trim()
      .toLowerCase();

    return dynamicEntityTypes.find((type) => {
      const value = String(type?.value || '')
        .trim()
        .toLowerCase();
      const label = String(type?.label || '')
        .trim()
        .toLowerCase();
      return (
        normalizedEntityType === value ||
        normalizedEntityType === label ||
        normalizedEntityType === value.replace(/\s+/g, '-') ||
        normalizedEntityType === label.replace(/\s+/g, '-')
      );
    });
  }, [dynamicEntityTypes, entity_type]);

  if (matchedDynamicEntityType) {
    return (
      <DynamicEntityIcon
        icon_type={matchedDynamicEntityType?.icon_type}
        icon_name={
          matchedDynamicEntityType?.icon_name || matchedDynamicEntityType?.icon
        }
        light_icon={matchedDynamicEntityType?.light_icon}
        dark_icon={matchedDynamicEntityType?.dark_icon}
        size={size}
        wrapperSize={wrapperSize}
        showBackground={showBackground}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '100%',
        background: theme?.palette?.grey?.[200],
        width: `${wrapperSize}px`,
        height: `${wrapperSize}px`,
      }}
    >
      <AttributeIcon
        style={{
          width: `${size}px`,
          height: `${size}px`,
          color: theme?.palette?.text?.secondary,
        }}
      />
    </Box>
  );
};
EntityIcon.propTypes = {
  entity_type: PropTypes.string.isRequired,
  request_type: PropTypes.string,
  size: PropTypes.number,
  wrapperSize: PropTypes.number,
  showBackground: PropTypes.bool,
};

const TitleComponent = ({ data, theme, entityName }) => {
  // Returns the badge background and text color tokens for the given alert severity flag.
  const getBadgeColors = useCallback(
    (flag) => {
      switch (flag) {
        case 'Low':
          return {
            bg: theme?.palette?.examinationResults?.severityLowBg,
            color: theme?.palette?.examinationResults?.severityLow,
          };
        case 'Medium':
          return {
            bg: theme?.palette?.examinationResults?.severityMediumBg,
            color: theme?.palette?.examinationResults?.severityMedium,
          };
        case 'High':
          return {
            bg: theme?.palette?.examinationResults?.severityHighBg,
            color: theme?.palette?.examinationResults?.severityHigh,
          };
        case 'Critical':
          return {
            bg: theme?.palette?.examinationResults?.severityCriticalBg,
            color: theme?.palette?.examinationResults?.severityCritical,
          };
        default:
          return {
            bg: theme?.palette?.grey?.[200],
            color: theme?.palette?.text?.primary,
          };
      }
    },
    [theme]
  );

  const renderRow = useCallback(
    (label, value, titleMaxWidth = '150px') => (
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          variant="body5"
          sx={{
            paddingRight: '8px',
            color: theme?.palette?.landscape?.tooltipLabelColor,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body4"
          title={value || ''}
          sx={{
            textAlign: 'right',
            maxWidth: titleMaxWidth,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: theme?.palette?.grey?.T80,
          }}
        >
          {value || ' '}
        </Typography>
      </Box>
    ),
    [theme]
  );

  return (
    <Card
      className="landscape-title-card"
      sx={{
        borderColor: theme?.palette?.other?.borderColor,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <EntityIcon
            entity_type={data?.entity_type}
            request_type={data?.request_type}
          />
          <Typography
            variant="body4"
            title={data?.unique_id ?? data?.entity_id}
            sx={{
              color: theme?.palette?.grey?.T80,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {data?.unique_id ?? data?.entity_id}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            backgroundColor: getBadgeColors(data?.Flag).bg,
            padding: '2px 8px',
            borderRadius: '16px',
          }}
        >
          <AlertHealthIndicator flag={data?.Flag} iconSize="8px" />
          <Typography
            variant="body5"
            sx={{ color: getBadgeColors(data?.Flag).color }}
          >
            {data?.Flag}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {renderRow('Name', entityName)}
        {renderRow('Status', data?.status)}
        {renderRow('Assigned To', data?.assignedUser, '200px')}
        {renderRow('Template', data?.template_type)}
        {renderRow('Workflow', data?.workflowLabel)}
      </Box>
    </Card>
  );
};
TitleComponent.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  theme: PropTypes.instanceOf(Object).isRequired,
  entityName: PropTypes.instanceOf(Object).isRequired,
};

export const SingleEntityNode = memo((props) => {
  const data = props?.data?.properties;
  const theme = useTheme();
  const entityName = props?.data?.label;
  const entityId = data?.unique_id;

  const { show: showContextMenu } = useContextMenu();
  const handleContextMenu = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      showContextMenu({
        id: 'node-rightClick',
        event: e,
        props,
      });
    },
    [props, showContextMenu]
  );

  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            p: 0,
          },
        },
      }}
      title={
        <TitleComponent data={data} theme={theme} entityName={entityName} />
      }
    >
      <Card
        onContextMenu={handleContextMenu}
        className="landscape-entity-node"
        sx={{
          position: 'relative',
          borderColor: theme?.palette?.primary?.main,
          ...(props?.type === 'main_entity_node'
            ? { backgroundColor: theme?.palette?.landscape?.mainNode }
            : { backgroundColor: theme?.palette?.landscape?.bg?.main }),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <EntityIcon
            entity_type={data?.entity_type}
            request_type={data?.request_type}
            {...(props?.type === 'main_entity_node'
              ? { size: 14, wrapperSize: 20 }
              : {})}
          />
          <Typography
            variant="body3Semibold"
            sx={{
              color: theme?.palette?.text?.secondary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '12px',
            }}
          >
            {entityId}
          </Typography>
        </Box>
        <Handle
          type="source"
          position={Position.Right}
          id="source-uni"
          isConnectable={true}
          style={{
            top: '30%',
            opacity: 0,
          }}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="source-bi"
          isConnectable={true}
          style={{
            top: '70%',
            opacity: 0,
          }}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="b"
          isConnectable={true}
          style={{
            top: '50%',
            opacity: 0,
          }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="target-uni"
          isConnectable={true}
          style={{
            top: '30%',
            opacity: 0,
          }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="target-bi"
          isConnectable={true}
          style={{
            top: '70%',
            opacity: 0,
          }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="b"
          isConnectable={true}
          style={{
            top: '50%',
            opacity: 0,
          }}
        />
      </Card>
    </Tooltip>
  );
});
SingleEntityNode.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  type: PropTypes.string,
};

// this component will handle cluster_node node type

/**
 * EntityMenuItem - a single item inside the entity Select dropdown.
 * Extracted to avoid arrow functions in JSX event handler props.
 */
const EntityMenuItem = memo(
  ({ entity, uniqueId, count, showContextMenu, theme, zoom = 1, ...rest }) => {
    const handleContextMenu = useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        showContextMenu({ id: 'node-rightClick', event: e, props: entity });
      },
      [entity, showContextMenu]
    );

    return (
      <MenuItem
        value={entity.id}
        onContextMenu={handleContextMenu}
        sx={{
          py: `${10 * zoom}px`,
          px: `${12 * zoom}px`,
          borderBottom: `1px solid ${theme?.palette?.other?.borderColor}`,
          '&:first-of-type': {
            borderTop: `1px solid ${theme?.palette?.other?.borderColor}`,
          },
          '&:hover': {
            backgroundColor: theme?.palette?.landscape?.entityMenuItemHover,
          },
          '&.Mui-selected': {
            backgroundColor: theme?.palette?.landscape?.entityMenuItemHover,
          },
          '&.Mui-selected:hover': {
            backgroundColor: theme?.palette?.landscape?.entityMenuItemHover,
          },
        }}
        {...rest}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              overflow: 'hidden',
            }}
          >
            <EntityIcon
              entity_type={entity?.entity_type}
              size={Math.round(13 * zoom)}
              wrapperSize={Math.round(20 * zoom)}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                overflow: 'hidden',
                minWidth: 0,
              }}
            >
              <Typography
                variant="body3Semibold"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: theme?.palette?.text?.primary,
                  fontSize: `${13 * zoom}px`,
                }}
              >
                {uniqueId}
              </Typography>
              <Tooltip
                slotProps={{
                  tooltip: {
                    sx: { p: 0 },
                  },
                }}
                title={
                  <TitleComponent
                    data={entity?.data?.properties}
                    theme={theme}
                    entityName={
                      entity?.data?.label || entity?.data?.properties?.unique_id
                    }
                  />
                }
              >
                <InfoOutlinedIcon
                  width={16 * zoom}
                  height={16 * zoom}
                  style={{
                    color: theme?.palette?.text?.secondary,
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            </Box>
          </Box>
          {count !== undefined && count !== null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexShrink: 0,
              }}
            >
              <DescriptionOutlinedIcon
                width={16 * zoom}
                height={16 * zoom}
                style={{
                  color: theme?.palette?.landscape?.iconMuted,
                }}
              />
              <Typography
                variant="body3Semibold"
                sx={{
                  color: theme?.palette?.text?.primary,
                  fontSize: `${13 * zoom}px`,
                }}
              >
                {count}
              </Typography>
            </Box>
          )}
        </Box>
      </MenuItem>
    );
  }
);
EntityMenuItem.propTypes = {
  entity: PropTypes.instanceOf(Object).isRequired,
  uniqueId: PropTypes.string,
  count: PropTypes.number,
  showContextMenu: PropTypes.func.isRequired,
  theme: PropTypes.instanceOf(Object).isRequired,
  zoom: PropTypes.number,
};

// Derives a deterministic display color from a node id string using a simple hash against a colors array.
const getColorFromId = (id, colors) => {
  let hash = 0;
  const str = String(id || '');
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const viewAllRowShape = PropTypes.shape({
  original: PropTypes.shape({
    unique_id: PropTypes.string,
    entityPath: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    template: PropTypes.string,
    templatePath: PropTypes.string,
    status: PropTypes.string,
    workflow: PropTypes.string,
    workflowPath: PropTypes.string,
    createdAt: PropTypes.string,
  }),
});

// Builds the edit-template URL for a "View All" row, or '' when the API row has no template ids.
const getTemplatePath = (templateId, versionId) => {
  if (!templateId || !versionId) return '';

  return getEditTemplatePath(templateId, versionId);
};

// Builds the edit-workflow URL for a "View All" row, or '' when the API row has no workflow.
const getWorkflowPath = (workflowLabel, workflowId) => {
  if (!workflowLabel || !workflowId) return '';

  const pathname = DEFAULT_ROUTE_CONFIG.editWorkflow.path
    .replace(':workflowType', encodeURIComponent(workflowLabel))
    .replace(':workflowId', workflowId);

  return `/${pathname}`;
};

// Renders "View All" cell text that opens `path` in a new tab; plain text when there is no path.
const ViewAllLinkText = ({ label, path }) => {
  const handleOpen = useCallback(() => {
    window.open(path, '_blank', 'noopener,noreferrer');
  }, [path]);

  if (!path) {
    return <Typography variant="body2">{label}</Typography>;
  }

  return (
    <Typography
      variant="body2"
      sx={{ color: 'primary.main' }}
      onClick={handleOpen}
    >
      {label}
    </Typography>
  );
};
ViewAllLinkText.propTypes = {
  label: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
};

// Renders the entity unique_id in the Id column of the "View All" table, linking to the entity.
const ViewAllIdCell = ({ row }) => (
  <ViewAllLinkText
    label={row.original.unique_id}
    path={row.original.entityPath}
  />
);
ViewAllIdCell.propTypes = { row: viewAllRowShape.isRequired };

// Renders the entity name in the Name column of the "View All" table, linking to the entity.
const ViewAllNameCell = ({ row }) => (
  <ViewAllLinkText label={row.original.name} path={row.original.entityPath} />
);
ViewAllNameCell.propTypes = { row: viewAllRowShape.isRequired };

// Renders the entity description in the Description column of the "View All" table.
const ViewAllDescriptionCell = ({ row }) => (
  <Typography
    variant="body2"
    title={row.original.description || ''}
    sx={{
      maxWidth: '220px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}
  >
    {row.original.description}
  </Typography>
);
ViewAllDescriptionCell.propTypes = { row: viewAllRowShape.isRequired };

// Renders the template type in the Template column of the "View All" table, linking to the template.
const ViewAllTemplateCell = ({ row }) => (
  <ViewAllLinkText
    label={row.original.template}
    path={row.original.templatePath}
  />
);
ViewAllTemplateCell.propTypes = { row: viewAllRowShape.isRequired };

// Renders the entity status as a Chip badge in the Status column of the "View All" table.
const ViewAllStatusCell = ({ row }) => (
  <Chip
    className="landscape-status-chip"
    label={row.original.status}
    size="small"
  />
);
ViewAllStatusCell.propTypes = { row: viewAllRowShape.isRequired };

// Renders the workflow label in the Workflow column of the "View All" table, linking to the workflow.
const ViewAllWorkflowCell = ({ row }) => (
  <ViewAllLinkText
    label={row.original.workflow}
    path={row.original.workflowPath}
  />
);
ViewAllWorkflowCell.propTypes = { row: viewAllRowShape.isRequired };

// Renders the formatted creation date in the Created At column of the "View All" table.
const ViewAllCreatedAtCell = ({ row }) => (
  <Typography variant="body2">{row.original.createdAt}</Typography>
);
ViewAllCreatedAtCell.propTypes = { row: viewAllRowShape.isRequired };

const VIEW_ALL_COLUMNS = [
  {
    heading: 'Id',
    accessor: 'unique_id',
    disableSortBy: false,
    width: 90,
    Cell: ViewAllIdCell,
  },
  {
    heading: 'Name',
    accessor: 'name',
    disableSortBy: false,
    Cell: ViewAllNameCell,
  },
  {
    heading: 'Description',
    accessor: 'description',
    disableSortBy: false,
    Cell: ViewAllDescriptionCell,
  },
  {
    heading: 'Template',
    accessor: 'template',
    disableSortBy: false,
    Cell: ViewAllTemplateCell,
  },
  {
    heading: 'Status',
    accessor: 'status',
    disableSortBy: true,
    Cell: ViewAllStatusCell,
  },
  {
    heading: 'Workflow',
    accessor: 'workflow',
    disableSortBy: false,
    Cell: ViewAllWorkflowCell,
  },
  {
    heading: 'Created At',
    accessor: 'createdAt',
    disableSortBy: false,
    Cell: ViewAllCreatedAtCell,
  },
];

// Returns default header cell props (left-aligned text) for every column in the "View All" table.
const getViewAllHeaderProps = () => ({ align: 'left' });

export const MultiEntityNode = memo((props) => {
  const { data } = props;
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [entityMenuWidth, setEntityMenuWidth] = useState(undefined);
  const entitySelectRef = React.useRef(null);
  const theme = useTheme();

  const { show: showContextMenu } = useContextMenu();
  const { zoom } = useViewport();
  const scrollBoxRef = useRef(null);
  const entityTypes = useMemo(() => getFilteredEntityTypes() || [], []);
  const entityTypeMap = useMemo(
    () => createEntityTypeMap(entityTypes),
    [entityTypes]
  );

  const associated_entity = data?.associated_entities || {};
  const relation = data?.relation;

  const templates = useMemo(() => {
    return Object.keys(associated_entity);
  }, [associated_entity]);

  //current node entityTypeLabel
  const entityTypeLabel = getEntityLabelById(
    associated_entity?.[selectedTemplate]?.[0]?.entity_type
  );

  //if data?.level > 0 then downstream otherwise upstream
  const cardTitle =
    data?.level > 0 ? relation?.name || 'Entities' : entityTypeLabel;

  // Initialise template to alphabetically first on mount / when templates change
  useEffect(() => {
    if (templates.length > 0) {
      const sortedArr = [...templates].sort((a, b) => a.localeCompare(b));
      setSelectedTemplate(sortedArr[0]);
    }
  }, [templates]);

  // Sync selectedEntityId from controlled prop (upstream auto-selection from NewLandscape)
  useEffect(() => {
    if (data?.selectedEntityId !== undefined) {
      setSelectedEntityId(data.selectedEntityId || '');
    }
  }, [data?.selectedEntityId]);

  // All entities for the selected template (before parent filtering)
  const allTemplateArtifacts = useMemo(() => {
    return associated_entity?.[selectedTemplate] || [];
  }, [selectedTemplate, associated_entity]);

  // Entities filtered by upstream parent selection when filteredByParentEntityIds is set
  const selectedTemplateArtifacts = useMemo(() => {
    const filteredParentIds = data?.filteredByParentEntityIds;
    if (filteredParentIds?.length > 0) {
      return allTemplateArtifacts.filter((entity) =>
        entity.parent_id?.some((pid) => filteredParentIds.includes(pid))
      );
    }
    return allTemplateArtifacts;
  }, [allTemplateArtifacts, data?.filteredByParentEntityIds]);

  // Total count used for "View All" – always from the full template list
  const totalCount = useMemo(
    () => allTemplateArtifacts.length,
    [allTemplateArtifacts]
  );

  const allAssociatedEntities = useMemo(
    () => Object.values(associated_entity).flat(),
    [associated_entity]
  );

  const entityById = useMemo(() => {
    const entityMap = new Map();

    allAssociatedEntities.forEach((entity) => {
      if (entity?.id) {
        entityMap.set(entity.id, entity);
      }
    });

    return entityMap;
  }, [allAssociatedEntities]);

  // entity_type from the first entity in the cluster (used for dot color + placeholder icon)
  const firstEntityType = useMemo(() => {
    return allAssociatedEntities[0]?.entity_type;
  }, [allAssociatedEntities]);

  // Dot color derived deterministically from the node id for visual distinctness
  const entityTypeDotColor = useMemo(
    () =>
      getColorFromId(props.id, theme?.palette?.landscape?.nodeDotColors || []),
    [props.id, theme]
  );

  const handleTemplateChange = useCallback(
    (event) => {
      const value = event?.target?.value;
      data?.handleSetTemplate?.({ [data.id]: value });
      setSelectedTemplate(value);
      setSelectedEntityId('');
      // Clear downstream filtering when template changes
      data?.handleEntitySelect?.(props.id, '', []);
    },
    [data, props.id, setSelectedTemplate, setSelectedEntityId]
  );

  // Updates the selected entity and propagates the selection to adjacent nodes via handleEntitySelect.
  const handleEntityChange = useCallback(
    (event) => {
      const entityId = event?.target?.value;
      const entity = selectedTemplateArtifacts.find((e) => e.id === entityId);
      setSelectedEntityId(entityId);
      data?.handleEntitySelect?.(props.id, entityId, entity?.parent_id || []);
    },
    [selectedTemplateArtifacts, data, props.id, setSelectedEntityId]
  );

  // Captures the dropdown trigger's current width so the entity menu can match it.
  const handleEntitySelectOpen = useCallback(() => {
    if (entitySelectRef.current) {
      setEntityMenuWidth(entitySelectRef.current.getBoundingClientRect().width);
    }
  }, [setEntityMenuWidth]);

  // Opens the "View All" dialog listing every entity in this cluster node.
  const handleViewAllOpen = useCallback(
    (e) => {
      e.stopPropagation();
      setViewAllOpen(true);
    },
    [setViewAllOpen]
  );

  // Closes the "View All" dialog.
  const handleViewAllClose = useCallback(
    () => setViewAllOpen(false),
    [setViewAllOpen]
  );

  // Renders the selected template name as a truncated tooltip text inside the template Select control.
  const renderTemplateSelectValue = useCallback(
    (value) => (
      <Tooltip title={value}>
        <Typography
          sx={{
            fontSize: '10.2px',
            lineHeight: '15.3px',
            color: theme?.palette?.text?.secondary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </Typography>
      </Tooltip>
    ),
    [theme]
  );

  // Stops click events from bubbling out of the "View All" dialog into the React Flow canvas.
  const handleDialogClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Triggers expansion of the selected entity's child nodes when the info icon is clicked.
  const handleInfoIconClick = useCallback(
    (e) => {
      e.stopPropagation();
      const entity = entityById.get(selectedEntityId);
      if (entity) {
        data?.handleNodeEntityClick?.({
          hidden: false,
          cluster_node: { ...entity, level: data?.level, node_id: props.id },
        });
      }
    },
    [selectedEntityId, entityById, data, props.id]
  );

  // Renders the entity Select's display value: a placeholder with type icon when empty, or the selected entity's id with an info-icon tooltip.
  const renderEntitySelectValue = useCallback(
    (value) => {
      if (!value) {
        const placeholderText = 'Select ' + cardTitle;
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              overflow: 'hidden',
              minWidth: 0,
            }}
          >
            {firstEntityType && (
              <EntityIcon
                entity_type={firstEntityType}
                size={13}
                wrapperSize={20}
              />
            )}
            <Tooltip title={placeholderText} placement="top">
              <Typography
                variant="body3"
                sx={{
                  color: theme?.palette?.text?.secondary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {placeholderText}
              </Typography>
            </Tooltip>
          </Box>
        );
      }
      const entity = entityById.get(value);
      const uniqueId = entity
        ? entity?.data?.properties?.unique_id || entity?.data?.label
        : value;
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            overflow: 'hidden',
          }}
        >
          {entity && (
            <EntityIcon
              entity_type={entity?.entity_type}
              size={13}
              wrapperSize={20}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              overflow: 'hidden',
              flex: 1,
            }}
          >
            <Tooltip title={uniqueId || ''}>
              <Typography
                variant="body3Semibold"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {uniqueId}
              </Typography>
            </Tooltip>
            <Tooltip
              slotProps={{
                tooltip: {
                  sx: { p: 0 },
                },
              }}
              title={
                entity ? (
                  <TitleComponent
                    data={entity?.data?.properties}
                    theme={theme}
                    entityName={
                      entity?.data?.label || entity?.data?.properties?.unique_id
                    }
                  />
                ) : (
                  ''
                )
              }
            >
              <InfoOutlinedIcon
                onClick={handleInfoIconClick}
                width={16}
                height={16}
                sx={{
                  color: theme?.palette?.text?.secondary,
                  flexShrink: 0,
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          </Box>
        </Box>
      );
    },
    [firstEntityType, entityById, theme, handleInfoIconClick, cardTitle]
  );

  const entityRouteDataById = useMemo(() => {
    const routeDataMap = new Map();

    allAssociatedEntities.forEach((entity) => {
      const uniqueId =
        entity?.data?.properties?.unique_id || entity?.data?.label || entity.id;
      const matchedEntityType = entityTypeMap.get(
        normalizeEntityTypeKey(entity?.entity_type)
      );
      const entityTypeSegment = matchedEntityType?.label || entity?.entity_type;

      if (entity?.id && uniqueId && entityTypeSegment) {
        routeDataMap.set(entity.id, { entityTypeSegment, uniqueId });
      }
    });

    return routeDataMap;
  }, [allAssociatedEntities, entityTypeMap]);

  const getEntityRouteById = useCallback(
    (entityId) => {
      const routeData = entityRouteDataById.get(entityId);
      if (!routeData) {
        return null;
      }
      return getEntityDetailsRoutePath(
        routeData.entityTypeSegment,
        encodeURIComponent(routeData.uniqueId)
      );
    },
    [entityRouteDataById, getEntityDetailsRoutePath]
  );

  // Transforms allTemplateArtifacts into flattened row objects for the "View All" table.
  const viewAllTableData = useMemo(
    () =>
      allTemplateArtifacts.map((entity) => {
        const p = entity?.data?.properties;
        return {
          unique_id: p?.unique_id || '',
          entityPath: getEntityRouteById(entity?.id) || '',
          name: p?.entity_name || '',
          description: p?.description || '',
          template: p?.template_type || '',
          templatePath: getTemplatePath(p?.template_id, p?.template_version_id),
          status: p?.status || '',
          workflow: p?.workflowLabel || '',
          workflowPath: getWorkflowPath(p?.workflowLabel, p?.workflow_id),
          createdAt: p?.created_at
            ? new Date(p.created_at).toLocaleString()
            : '',
        };
      }),
    [allTemplateArtifacts, getEntityRouteById]
  );

  const selectedEntityRoute = useMemo(
    () => getEntityRouteById(selectedEntityId),
    [selectedEntityId]
  );

  const openSelectedEntity = useCallback(
    (event) => {
      event?.stopPropagation();
      if (!selectedEntityRoute) {
        return;
      }

      window.open(selectedEntityRoute, '_blank', 'noopener,noreferrer');
    },
    [selectedEntityRoute]
  );

  return (
    <Card
      className="landscape-cluster-node"
      onDoubleClickCapture={openSelectedEntity}
      sx={{
        position: 'relative',
        borderColor: theme?.palette?.entityOverview?.dividerBorder,
        backgroundColor: theme?.palette?.landscape?.bg?.main,
      }}
    >
      {/* Header row: colored dot + relation name (left) + template label or dropdown (right) */}
      <Box
        ref={scrollBoxRef}
        className="nodrag nopan"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '24px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            flex: 1,
            overflow: 'hidden',
          }}
        >
          <Tooltip
            slotProps={{
              tooltip: {
                sx: { p: 0 },
              },
            }}
            title={
              <Card className="landscape-relation-tooltip">
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px',
                  }}
                >
                  <Typography
                    variant="body5"
                    sx={{ color: theme?.palette?.landscape?.tooltipLabelColor }}
                  >
                    Relation Type
                  </Typography>
                  <Typography
                    variant="body5"
                    sx={{ color: theme?.palette?.grey?.T80 }}
                  >
                    {relation?.is_bidirectional
                      ? 'Bi-Directional'
                      : 'Uni-Directional'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px',
                  }}
                >
                  <Typography
                    variant="body5"
                    sx={{ color: theme?.palette?.landscape?.tooltipLabelColor }}
                  >
                    Cardinality
                  </Typography>
                  <Typography
                    variant="body5"
                    sx={{ color: theme?.palette?.grey?.T80 }}
                  >
                    {relation?.cardinality || '—'}
                  </Typography>
                </Box>
              </Card>
            }
          >
            <Box
              sx={{
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                backgroundColor: entityTypeDotColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                cursor: 'default',
              }}
            >
              <Box
                sx={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: theme?.palette?.landscape?.bg?.main,
                  flexShrink: 0,
                }}
              />
            </Box>
          </Tooltip>
          <Tooltip title={cardTitle}>
            <Typography
              variant="body3"
              sx={{
                color: theme?.palette?.text?.secondary,
                fontSize: '10.2px',
                lineHeight: '15.3px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {cardTitle}
            </Typography>
          </Tooltip>
        </Box>

        {/* Show dropdown only when multiple templates exist, otherwise plain label */}
        {templates.length > 1 ? (
          <Select
            className="nodrag nopan landscape-template-select"
            value={selectedTemplate}
            onChange={handleTemplateChange}
            size="small"
            variant="standard"
            IconComponent={ExpandMoreIcon}
            renderValue={renderTemplateSelectValue}
            sx={{
              color: theme?.palette?.text?.secondary,
              '& .MuiSelect-select': {
                color: `${theme?.palette?.text?.secondary} !important`,
              },
              '& .MuiSelect-icon': {
                color: `${theme?.palette?.text?.secondary} !important`,
              },
            }}
            slotProps={{
              menu: {
                disablePortal: false,
                slotProps: {
                  paper: {
                    style: {
                      width: '117px',
                      minWidth: '117px',
                      maxWidth: '117px',
                    },
                    sx: {
                      backgroundColor: theme?.palette?.background?.paper,
                    },
                  },
                },
              },
            }}
          >
            {templates.map((t) => (
              <MenuItem key={t} value={t}>
                <Tooltip title={t}>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      lineHeight: '22px',
                      letterSpacing: 0,
                      color: theme?.palette?.text?.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t}
                  </Typography>
                </Tooltip>
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Tooltip title={selectedTemplate}>
            <Typography
              variant="body3"
              sx={{
                color: theme?.palette?.text?.secondary,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                fontSize: '10.2px',
                lineHeight: '15.3px',
              }}
            >
              {selectedTemplate}
            </Typography>
          </Tooltip>
        )}
      </Box>
      {/* Entity select dropdown with linked_entity_count */}
      <Select
        ref={entitySelectRef}
        className="nodrag nopan landscape-entity-select"
        value={selectedEntityId}
        onChange={handleEntityChange}
        onOpen={handleEntitySelectOpen}
        size="small"
        displayEmpty
        renderValue={renderEntitySelectValue}
        IconComponent={ExpandMoreIcon}
        MenuProps={{
          disablePortal: false,
          slotProps: {
            paper: {
              style: entityMenuWidth
                ? {
                    minWidth: entityMenuWidth,
                    maxWidth: entityMenuWidth,
                    maxHeight: '40vh',
                    overflowY: 'auto',
                  }
                : { maxHeight: '40vh', overflowY: 'auto' },
              sx: {
                p: '8px',
                marginTop: '4px',
                border: `1px solid ${theme?.palette?.other?.borderColor}`,
                borderRadius: '6px',
                backgroundColor: theme?.palette?.background?.paper,
                '& .MuiList-root': {
                  p: 0,
                },
                '& .MuiMenuItem-root.Mui-selected': {
                  backgroundColor:
                    theme?.palette?.landscape?.entityMenuItemHover,
                  color: theme?.palette?.text?.primary,
                },
                '& .MuiMenuItem-root.Mui-selected:hover': {
                  backgroundColor:
                    theme?.palette?.landscape?.entityMenuItemHover,
                },
                '& .MuiMenuItem-root:hover': {
                  backgroundColor:
                    theme?.palette?.landscape?.entityMenuItemHover,
                },
              },
            },
          },
        }}
        sx={{
          backgroundColor: theme?.palette?.background?.paper,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme?.palette?.other?.borderColor,
            borderRadius: '8px',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme?.palette?.other?.borderColor} !important`,
            borderWidth: '1px !important',
          },
          '&.Mui-focused': {
            borderColor: `${theme?.palette?.other?.borderColor} !important`,
            borderWidth: '1px !important',
          },
          '& .MuiSelect-icon': {
            color: `${theme?.palette?.text?.secondary} !important`,
          },
        }}
      >
        {selectedTemplateArtifacts.map((entity) => {
          const uniqueId =
            entity?.data?.properties?.unique_id || entity?.data?.label;
          const count = entity?.data?.properties?.linked_entity_count;
          return (
            <EntityMenuItem
              key={entity.id}
              value={entity.id}
              entity={entity}
              uniqueId={uniqueId}
              count={count}
              showContextMenu={showContextMenu}
              theme={theme}
              zoom={zoom}
            />
          );
        })}
      </Select>

      {/* View All link */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Typography
          className="nodrag nopan"
          variant="captionMedium"
          sx={{
            color: theme?.palette?.primary?.main,
            cursor: 'pointer',
            letterSpacing: '0.03px',
          }}
          onClick={handleViewAllOpen}
        >
          {`View All  (${totalCount})`}
        </Typography>
      </Box>

      <Handle
        type="source"
        position={Position.Right}
        id="source-uni"
        isConnectable={true}
        style={{
          top: '30%',
          opacity: 0,
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="source-bi"
        isConnectable={true}
        style={{
          top: '70%',
          opacity: 0,
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="b"
        isConnectable={true}
        style={{
          top: '50%',
          opacity: 0,
        }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="target-uni"
        isConnectable={true}
        style={{
          top: '30%',
          opacity: 0,
        }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="target-bi"
        isConnectable={true}
        style={{
          top: '70%',
          opacity: 0,
        }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="b"
        isConnectable={true}
        style={{
          top: '50%',
          opacity: 0,
        }}
      />

      {/* View All Dialog – rendered via Portal, outside React Flow canvas */}
      {viewAllOpen && (
        <Dialog
          open={viewAllOpen}
          onClose={handleViewAllClose}
          maxWidth="md"
          fullWidth
          onClick={handleDialogClick}
        >
          <DialogTitle>
            <Box component="span" aria-live="polite" aria-atomic="true">
              {'All ' + cardTitle}
            </Box>
          </DialogTitle>
          <IconButton
            onClick={handleViewAllClose}
            size="small"
            sx={{
              position: 'absolute',
              right: 16,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers sx={{ px: '24px', py: '16px' }}>
            <ReactMuiTableListViewV8
              columns={VIEW_ALL_COLUMNS}
              data={viewAllTableData}
              getHeaderProps={getViewAllHeaderProps}
              enablePagination
              showPaginationAction
            />
          </DialogContent>
          <Divider />
          <DialogActions
            sx={{ px: '24px', py: '10px', justifyContent: 'flex-end' }}
          >
            <SolyticsButton variant="contained" onClick={handleViewAllClose}>
              Close
            </SolyticsButton>
          </DialogActions>
        </Dialog>
      )}
    </Card>
  );
});
MultiEntityNode.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.instanceOf(Object).isRequired,
};

// info: not completed from backend side yet
export const AttributeNode = memo((props) => {
  const { data } = props;
  const theme = useTheme();
  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: theme?.palette?.primary?.main,
        borderRadius: '8px',
        width: '150px',
      }}
    >
      <List
        sx={{
          padding: '8px',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: `${theme?.palette?.landscape?.bg?.main} !important`,
        }}
      >
        {Object.keys(data?.properties || {}).map((key) => {
          const label = data?.properties[key]?.label;
          return (
            <ListItemButton
              button
              key={key}
              sx={{
                display: 'flex',
                width: '100%',
                overflow: 'hidden',
                padding: '4px',
                gap: '8px',
                borderRadius: '4px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '100%',
                  backgroundColor: theme?.palette?.landscape?.bg?.attribute,
                  width: '30px',
                  height: '30px',
                }}
              >
                <AttributeIcon
                  style={{
                    width: '20px',
                    height: '20px',
                    color: theme?.palette?.landscape?.attribute,
                  }}
                />
              </Box>
              <Typography
                variant="body3"
                sx={{
                  color: theme?.palette?.primary?.main,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {upperCase(label)}
              </Typography>
            </ListItemButton>
          );
        })}
      </List>
      <Handle
        type="source"
        position={Position.Right}
        id="b"
        isConnectable={true}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="b"
        isConnectable={true}
      />
    </Card>
  );
});
AttributeNode.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
};

export const LineageCardNode = memo((props) => {
  const data = props?.data?.properties;
  const theme = useTheme();
  const entityName = props?.data?.entity_alias || props?.data?.label;
  const entityId = data?.unique_id || props?.data?.entity_id;

  const { show: showContextMenu } = useContextMenu();
  const handleContextMenu = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      showContextMenu({
        id: 'node-rightClick',
        event: e,
        props,
      });
    },
    [props, showContextMenu]
  );

  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            p: 0,
          },
        },
      }}
      title={
        <TitleComponent data={data} theme={theme} entityName={entityName} />
      }
    >
      <Card
        onContextMenu={handleContextMenu}
        className="landscape-lineage-node"
        sx={{
          position: 'relative',
          borderColor: theme?.palette?.primary?.main,
          ...(props?.type === 'main_entity_node'
            ? { backgroundColor: theme?.palette?.landscape?.mainNode }
            : { backgroundColor: theme?.palette?.landscape?.bg?.main }),
        }}
      >
        <Typography
          variant="body3"
          sx={{
            color: theme?.palette?.primary?.main,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {entityName}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <EntityIcon
            entity_type={props?.data?.entity_type || data?.entity_type}
            request_type={props?.data?.request_type || data?.request_type}
          />
          <Typography
            variant="body3"
            sx={{
              color: theme?.palette?.secondary?.light,
            }}
          >
            {entityId || 'No ID'}
          </Typography>
        </Box>
        <Handle
          type="source"
          position={Position.Right}
          id="source-uni"
          style={{
            top: '30%',
            bottom: 'auto',
            background: theme?.palette?.landscape?.lineageHandleColor,
          }}
          isConnectable={true}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="source-bi"
          style={{
            top: '70%',
            bottom: 'auto',
            background: theme?.palette?.landscape?.lineageHandleColor,
          }}
          isConnectable={true}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="target-left"
          style={{
            top: '50%',
            bottom: 'auto',
            background: theme?.palette?.landscape?.lineageHandleColor,
          }}
          isConnectable={true}
        />
      </Card>
    </Tooltip>
  );
});
LineageCardNode.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  type: PropTypes.string,
};
